import { Brandbook } from '@agently/types';
import { updateCssVariables } from '@agently/utils';
import { useStore } from '@nanostores/react';
import { persistentAtom } from '@nanostores/persistent';

const BRAND_BOOK_CACHE_KEY = 'agently:mobile:brandbook:v1';
const BRAND_BOOK_CACHE_VERSION = 1 as const;

type CachedBrandbook = {
  userId: number;
  value: Brandbook;
  version: typeof BRAND_BOOK_CACHE_VERSION;
};

const decodeCachedBrandbook = (value: string): CachedBrandbook | null => {
  try {
    const parsed = JSON.parse(value) as Partial<CachedBrandbook>;
    if (
      parsed.version !== BRAND_BOOK_CACHE_VERSION ||
      typeof parsed.userId !== 'number' ||
      !parsed.value ||
      typeof parsed.value !== 'object'
    ) {
      return null;
    }
    return parsed as CachedBrandbook;
  } catch {
    return null;
  }
};

const $cachedBrandbook = persistentAtom<CachedBrandbook | null>(
  BRAND_BOOK_CACHE_KEY,
  null,
  {
    decode: decodeCachedBrandbook,
    encode: JSON.stringify,
  },
);

const resetCssVariables = (brandbook: Brandbook) => {
  updateCssVariables(
    Object.fromEntries(Object.keys(brandbook).map((key) => [key, null])),
  );
};

const removeCachedBrandbook = () => {
  $cachedBrandbook.set(null);
  localStorage.removeItem(BRAND_BOOK_CACHE_KEY);
};

export const applyCachedBrandbook = () => {
  const cached = $cachedBrandbook.get();
  if (!localStorage.getItem('authToken') || !cached) {
    if (cached) {
      resetCssVariables(cached.value);
      removeCachedBrandbook();
    }
    return;
  }

  updateCssVariables(cached.value);
};

export const ensureBrandbookOwner = (userId: number) => {
  const cached = $cachedBrandbook.get();
  if (cached && cached.userId !== userId) {
    resetCssVariables(cached.value);
    removeCachedBrandbook();
  }
};

export const setCachedBrandbook = (
  userId: number,
  brandbook: Brandbook | null,
) => {
  const previous = $cachedBrandbook.get();
  if (!brandbook) {
    if (previous) {
      resetCssVariables(previous.value);
    }
    removeCachedBrandbook();
    return;
  }

  updateCssVariables(brandbook);
  $cachedBrandbook.set({
    userId,
    value: brandbook,
    version: BRAND_BOOK_CACHE_VERSION,
  });
};

export const clearCachedBrandbook = () => {
  const cached = $cachedBrandbook.get();
  if (cached) {
    resetCssVariables(cached.value);
  }
  removeCachedBrandbook();
};

export const useBrandbook = () => useStore($cachedBrandbook)?.value ?? null;
